<script>
import baseMixin from '@/mixins/baseMixin'
import { BookingFilters } from '@/shared/constants/booking/list/BookingFilters'
import FilterPanelItem from './filter-panel-item.vue'
import FilterSearchInput from './filter-search-input.vue'

const DefaultBookingFilter = BookingFilters.default.id

/**
 * Threshold for using virtual scroll.
 * Lists with more items than this will use v-virtual-scroll for performance.
 * Smaller lists will use a simple v-for with dynamic height.
 */
const VIRTUAL_SCROLL_THRESHOLD = 10

export default {
  name: 'FilterPanel',
  components: {
    FilterPanelItem,
    FilterSearchInput,
  },
  mixins: [baseMixin],
  props: {
    title: { type: String, default: '' },
    name: { type: String, default: '' },
    selectedFilter: { type: Number, default: DefaultBookingFilter },
    items: { type: Array, default: () => [] },
    valueKey: { type: String, required: true },
    labelKey: { type: [Array, String], required: true },
    localSelected: { type: Array, required: true },
    maxHeight: { type: Number, required: true },
    selectedCount: { type: Number, default: 0 },
    searchable: { type: Boolean },
  },
  emits: ['update:localSelected', 'clear', 'apply', 'back'],
  data() {
    return {
      internalSelected: [...this.localSelected],
      searchText: '',
    }
  },
  computed: {
    filteredItems() {
      if (!this.searchText) return this.items
      return this.items.filter((item) =>
        String(this.labelText(item))
          .toLowerCase()
          .includes(this.searchText.toLowerCase())
      )
    },
    itemHeight() {
      // Status filter has smaller rows (py-2 + mb-1), others have py-3
      return this.selectedFilter === BookingFilters.status.id ? 44 : 56
    },
    /**
     * Determines whether to use virtual scroll based on item count.
     * @returns {boolean} True if virtual scroll should be used
     */
    useVirtualScroll() {
      return this.filteredItems.length > VIRTUAL_SCROLL_THRESHOLD
    },
  },
  methods: {
    labelText(item) {
      return Array.isArray(this.labelKey)
        ? this.labelKey.map((k) => item[k]).join(' ')
        : item[this.labelKey]
    },
    applyFilter() {
      this.$emit('update:localSelected', [...this.internalSelected])
      this.$emit('apply')
    },
    clearSelection() {
      this.internalSelected = []
      this.$emit('clear')
    },
  },
}
</script>

<template>
  <div
    class="filter-panel"
    :data-filter="`filter-${title}`"
    :style="{ maxHeight: `${maxHeight + 109}px` }"
  >
    <div class="filter-title" style="justify-content: space-between">
      <div class="d-flex align-center mb-4">
        <v-icon size="20" class="me-2 cursor-pointer" @click="$emit('back')"
          >mdi-arrow-left</v-icon
        >
        <span class="filter-header">{{ title }}</span>
      </div>
    </div>

    <FilterSearchInput v-if="searchable" v-model="searchText" />

    <!-- Virtual scroll for large lists (performance) -->
    <v-virtual-scroll
      v-if="useVirtualScroll"
      ref="filterContainerBox"
      class="filter-container-box"
      :items="filteredItems"
      :item-height="itemHeight"
      :height="maxHeight"
      bench="5"
    >
      <template v-slot="{ item, index }">
        <FilterPanelItem
          :key="`filter-${name}-${item[valueKey]}`"
          v-model="internalSelected"
          :item="item"
          :index="index"
          :selected-filter="selectedFilter"
          :value-key="valueKey"
          :label-key="labelKey"
          :name="name"
        />
      </template>
    </v-virtual-scroll>

    <!-- Simple list for small item counts (dynamic height) -->
    <div v-else ref="filterContainerBox" class="filter-container-box">
      <FilterPanelItem
        v-for="(item, index) in filteredItems"
        :key="`filter-${name}-${item[valueKey]}`"
        v-model="internalSelected"
        :item="item"
        :index="index"
        :selected-filter="selectedFilter"
        :value-key="valueKey"
        :label-key="labelKey"
        :name="name"
      />
    </div>
    <div class="filter-footer">
      <div class="d-flex align-center justify-space-between w-100">
        <div class="flex-grow-1">
          <div
            v-show="selectedCount > 0"
            class="cursor-pointer clear-filter"
            :class="colorClass.text.primary400"
            :data-filter="`clear-${name}-filters`"
            @click="clearSelection"
          >
            {{ $t('topFilterBar.filter.clearAll') }}
          </div>
        </div>
        <BaseButton
          :border-radius="'8px'"
          :width="'70px'"
          :height="'30px'"
          class="p-5 apply-btn"
          :disabled="internalSelected.length === 0"
          @click="applyFilter"
        >
          {{ $t('general.apply') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/booking-top-filter-bar.scss';
</style>
